import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { clsx } from 'clsx';
import styles from './CodeInput.module.scss';
import { LoaderIcon } from '../../icons/Icon';

export interface CodeInputProps {
  /** Number of cells */
  length?: number;
  /** Current numeric code value */
  value?: string;
  /** Called when code changes */
  onChange?: (value: string) => void;
  /** Helper message below the cells */
  helperText?: string;
  /** Explicit error message; also toggles error styles */
  errorText?: string;
  /** Force error state */
  error?: boolean;
  /** Disable the entire input */
  disabled?: boolean;
  /** Whether the input is in a loading state */
  loading?: boolean;
  /** Autofocus first cell on mount */
  autoFocus?: boolean;
  /** Class name for the outer container */
  className?: string;
}

const isDigit = (ch: string) => /\d/.test(ch);

export function CodeInput({
  length = 6,
  value,
  onChange,
  helperText,
  errorText,
  error = false,
  disabled = false,
  loading = false,
  autoFocus = true,
  className,
}: CodeInputProps) {
  const [internalValue, setInternalValue] = useState<string>(value ?? '');
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (value !== undefined && value !== internalValue) setInternalValue(value);
  }, [value]);

  const codeChars = useMemo(() => {
    const result: string[] = new Array(length).fill('');
    const sliced = (internalValue || '').slice(0, length);
    for (let i = 0; i < sliced.length; i += 1) {
      result[i] = sliced[i] ?? '';
    }
    return result;
  }, [internalValue, length]);

  const setCodeAt = useCallback(
    (index: number, ch: string) => {
      const next = codeChars
        .map((c, i) => (i === index ? ch : c))
        .join('')
        .slice(0, length);
      setInternalValue(next);
      onChange?.(next);
    },
    [codeChars, length, onChange]
  );

  const focusCell = useCallback((index: number) => {
    const el = inputsRef.current[index];
    if (el) el.focus();
  }, []);

  const handleInputChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Handle SMS autocomplete: when the first input receives multiple characters
      if (index === 0 && inputValue.length > 1) {
        const digits = inputValue.replace(/\D/g, '').slice(0, length);
        if (digits.length > 0) {
          setInternalValue(digits);
          onChange?.(digits);
          // Focus the appropriate cell based on how many digits were filled
          const nextIndex = Math.min(digits.length, length - 1);
          requestAnimationFrame(() => focusCell(nextIndex));
        }
        return;
      }

      // Handle single character input
      const nextChar = inputValue.slice(-1);
      if (!nextChar) {
        setCodeAt(index, '');
        return;
      }
      if (!isDigit(nextChar)) return;
      setCodeAt(index, nextChar);
      if (index < length - 1) focusCell(index + 1);
    };

  const handleInputKeyDown =
    (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        if (!codeChars[index] && index > 0) focusCell(index - 1);
        else setCodeAt(index, '');
      }
      if (e.key === 'ArrowLeft' && index > 0) focusCell(index - 1);
      if (e.key === 'ArrowRight' && index < length - 1) focusCell(index + 1);
    };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (disabled || loading) return;
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    setInternalValue(pasted);
    onChange?.(pasted);
    const nextIndex = Math.min(pasted.length, length - 1);
    requestAnimationFrame(() => focusCell(nextIndex));
  };

  // Disabled inputs take no focus: once enabled, e.g. when loading ends.
  useEffect(() => {
    if (autoFocus && !disabled && !loading) {
      requestAnimationFrame(() => focusCell(0));
    }
  }, [autoFocus, disabled, loading]);

  const isErrored = error || !!errorText;
  const isFilled = internalValue.length > 0;
  const isDisabled = disabled || loading;

  return (
    <div
      className={clsx(styles.container, className, {
        [styles.error]: isErrored,
        [styles.disabled]: isDisabled,
        [styles.loading]: loading,
        [styles.filled]: isFilled,
      })}
      onPaste={handlePaste}
    >
      <div
        className={clsx(styles.cells, {
          [styles['cells--compact']]: length < 6,
        })}
        role="group"
        aria-disabled={isDisabled}
      >
        {codeChars.map((ch, i) => (
          <React.Fragment key={i}>
            <input
              className={clsx(styles.input, {
                [styles.error]: isErrored,
                [styles.disabled]: isDisabled,
                [styles.loading]: loading,
                [styles.cellFilled]: ch !== '',
              })}
              ref={(el) => {
                inputsRef.current[i] = el;
              }}
              id={`code-input-${i}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              autoFocus={i === 0 && autoFocus && !loading}
              value={ch}
              disabled={isDisabled}
              maxLength={i === 0 ? length : 1}
              onChange={handleInputChange(i)}
              onKeyDown={handleInputKeyDown(i)}
            />
            {length === 6 && i === 2 && (
              <div className={styles.separator} aria-hidden="true" />
            )}
          </React.Fragment>
        ))}
      </div>

      {(errorText || helperText) && (
        <div className={styles.helperRow}>
          <span
            className={clsx(styles.helperText, {
              [styles.errorText]: isErrored,
            })}
          >
            {errorText || helperText}
          </span>
        </div>
      )}

      {loading && (
        <div className={styles.loader} aria-hidden="true">
          <LoaderIcon className={styles.spinner} />
        </div>
      )}
    </div>
  );
}

CodeInput.displayName = 'CodeInput';

export default CodeInput;
